import { Grid, AlertTriangle } from 'lucide-react'
import { type Control, Controller, type FieldErrors, type UseFormWatch } from 'react-hook-form'

import { PORTAL_SECTIONS, SECTION_GROUPS, type SectionOption } from '@/constants/sections'
import {
  type CreateUserFormData,
  type UpdateUserFormData,
  type UserFormInput,
} from '@/lib/validation/user-schemas'

function handleSectionToggle(
  checked: boolean,
  sectionId: string,
  currentFieldValue: string | string[] | undefined,
  onChange: (value: string[]) => void
) {
  const currentValue = Array.isArray(currentFieldValue) ? currentFieldValue : []
  if (checked) {
    onChange([...currentValue, sectionId])
    return
  }
  onChange(currentValue.filter((id) => id !== sectionId))
}

interface PortalSectionsSectionProps {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  errors: FieldErrors<UserFormInput>
  watch: UseFormWatch<UserFormInput>
}

export function PortalSectionsSection({ control, errors, watch }: PortalSectionsSectionProps) {
  const sectionsAll = watch('attributes.sections_all')
  const selectedSections = watch('attributes.sections')
  const sectionsArray = Array.isArray(selectedSections) ? selectedSections : []

  const realmRoles = watch('realmRoles')
  const isRootOperator = realmRoles.cs_sys_op === true
  const isSysAdmin = realmRoles.cs_sys_admin === true
  const hasHighLevelRole = isRootOperator || isSysAdmin

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ${hasHighLevelRole ? 'opacity-50' : ''}`}
    >
      <div className="p-6 space-y-6">
        <SectionHeader />
        <HighLevelRoleBanner
          isRootOperator={isRootOperator}
          isSysAdmin={isSysAdmin}
          hasHighLevelRole={hasHighLevelRole}
        />

        <div className="space-y-4">
          <GrantAllToggle control={control} hasHighLevelRole={hasHighLevelRole} />
          <SectionCheckboxGrid
            control={control}
            sectionsAll={sectionsAll ?? false}
            sectionsArray={sectionsArray}
            hasHighLevelRole={hasHighLevelRole}
          />

          {errors.attributes?.sections && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {errors.attributes.sections.message}
            </p>
          )}
        </div>

        <SectionGuidelines />
      </div>
    </div>
  )
}

const SectionHeader = () => (
  <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
      Portal Sections Access
    </h3>
    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
      Control which sections of the portal this user can access
    </p>
  </div>
)

const HighLevelRoleBanner = ({
  isRootOperator,
  isSysAdmin,
  hasHighLevelRole,
}: {
  isRootOperator: boolean
  isSysAdmin: boolean
  hasHighLevelRole: boolean
}) => {
  if (!hasHighLevelRole) return null

  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-800 dark:text-blue-200">
          {isRootOperator && 'Root Operator has access to all portal sections'}
          {isSysAdmin && 'System Administrator has access to all portal sections'}
        </p>
      </div>
    </div>
  )
}

const GrantAllToggle = ({
  control,
  hasHighLevelRole,
}: {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  hasHighLevelRole: boolean
}) => (
  <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
    <label
      htmlFor="sections_all"
      className={`text-sm font-medium ${hasHighLevelRole ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'}`}
    >
      Section permissions
    </label>
    <div className="flex items-center">
      <Controller
        name="attributes.sections_all"
        control={control}
        render={({ field }) => (
          <input
            type="checkbox"
            id="sections_all"
            checked={field.value || false}
            onChange={(e) => {
              field.onChange(e.target.checked)
            }}
            onBlur={field.onBlur}
            ref={field.ref}
            disabled={hasHighLevelRole}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        )}
      />
      <label
        htmlFor="sections_all"
        className={`ml-2 text-sm ${hasHighLevelRole ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'text-gray-600 dark:text-gray-400'}`}
      >
        Grant access to all sections
      </label>
    </div>
  </div>
)

const SectionCheckboxGrid = ({
  control,
  sectionsAll,
  sectionsArray,
  hasHighLevelRole,
}: {
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  sectionsAll: boolean
  sectionsArray: string[]
  hasHighLevelRole: boolean
}) => (
  <div className="space-y-6">
    {SECTION_GROUPS.map(({ key, label }) => {
      const sectionsInGroup = PORTAL_SECTIONS.filter((s) => s.group === key)
      if (sectionsInGroup.length === 0) return null
      return (
        <fieldset key={key} className="space-y-3">
          <legend className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {label}
          </legend>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sectionsInGroup.map((section) => (
              <SectionCheckbox
                key={section.id}
                section={section}
                control={control}
                sectionsAll={sectionsAll}
                sectionsArray={sectionsArray}
                hasHighLevelRole={hasHighLevelRole}
              />
            ))}
          </div>
        </fieldset>
      )
    })}
  </div>
)

const SectionCheckbox = ({
  section,
  control,
  sectionsAll,
  sectionsArray,
  hasHighLevelRole,
}: {
  section: SectionOption
  control: Control<UserFormInput, unknown, CreateUserFormData | UpdateUserFormData>
  sectionsAll: boolean
  sectionsArray: string[]
  hasHighLevelRole: boolean
}) => (
  <Controller
    name="attributes.sections"
    control={control}
    render={({ field }) => {
      const isChecked = sectionsAll || sectionsArray.includes(section.id)
      const isDisabled = sectionsAll || hasHighLevelRole

      return (
        <div className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
          <input
            type="checkbox"
            id={`section-${section.id}`}
            checked={isChecked}
            disabled={isDisabled}
            onChange={(e) => {
              if (isDisabled) return
              handleSectionToggle(e.target.checked, section.id, field.value, field.onChange)
            }}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="flex-1 min-w-0">
            <label
              htmlFor={`section-${section.id}`}
              className={`block text-sm font-medium cursor-pointer ${isDisabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-900 dark:text-gray-100'}`}
            >
              {section.name}
            </label>
            <p className="mt-0.5 text-xs font-mono text-gray-400 dark:text-gray-500">
              {section.id}
            </p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{section.description}</p>
          </div>
        </div>
      )
    }}
  />
)

const SectionGuidelines = () => (
  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
    <div className="flex items-start gap-3">
      <Grid className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-blue-800 dark:text-blue-200">
        <p className="font-medium mb-1">Section access guidelines</p>
        <ul className="space-y-1 text-blue-700 dark:text-blue-300">
          <li>System Operators and System Admins automatically have access to all sections</li>
          <li>Select individual sections to grant specific access</li>
          <li>Check &quot;Grant access to all sections&quot; to enable all portal features</li>
        </ul>
      </div>
    </div>
  </div>
)
